<template>
  <section>
    <div
      class="px-4 mx-auto max-w-3xl lg:px-0"
      :class="[
        paddingTop === 'reduced' ? '' : 'pt-4 lg:pt-6',
        paddingBottom === 'reduced' ? 'pb-6 lg:pb-6' : 'pb-6 lg:pb-8'
      ]"
    >
      <div class="w-full">
        <div
          class="flex flex-col items-start w-full lg:flex-row lg:items-start"
          :class="[
            contentGap === 'medium' ? 'gap-6 lg:gap-6' : 'gap-6 lg:gap-16',
            imagePosition === 'right' ? 'lg:flex-row-reverse' : ''
          ]"
        >
          <div class="relative mx-auto w-full max-w-xs shrink-0 lg:mx-0">
            <nuxt-img
              v-if="imageSrc"
              :src="imageSrc"
              :alt="imageAlt"
              :width="imageWidth"
              :height="imageHeight"
              format="webp"
              loading="lazy"
              sizes="xs:100vw sm:320px lg:320px"
              class="object-cover w-full rounded-2xl aspect-[320/446]"
            />
            <div
              v-else
              class="w-full rounded-2xl aspect-[320/446] bg-[#2A2A2A]"
              aria-hidden="true"
            />
          </div>

          <div class="flex flex-col flex-1 gap-3 items-start min-w-0">
            <div
              v-if="badgeText"
              class="inline-flex items-start px-3.5 py-1 w-fit rounded border border-[#E0D5C5] bg-[#F0E8D8]"
            >
              <span
                class="text-xs tracking-wide text-[#6B3A22] uppercase font-ww-regular"
                v-html="badgeText"
              />
            </div>

            <h2
              v-if="title"
              class="text-22 font-bold leading-27 font-ww-bold text-[#3B2314]"
              v-html="title"
            />

            <div
              class="pt-0.5 pb-3 text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-0 [&_strong]:font-ww-bold [&_sup]:text-xs"
              v-html="content"
            />
          </div>
        </div>

        <div
          v-if="showDivider"
          class="mt-10 h-px w-full bg-[#E0D5C5]"
          aria-hidden="true"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  badgeText: {
    type: String,
    default: 'Scans and Tests Focus'
  },
  title: {
    type: String,
    default: 'Coronary Calcium Scan'
  },
  content: {
    type: String,
    default: `<p>You know the saying… "An ounce of prevention is worth a pound of cure."</p>
<p>And, this is essentially what a coronary calcium scan is.</p>
<p>This type of scan is basically a CT scan of your heart aimed at detecting early plaque buildup and producing a score which indicates your personal risk of heart attack.</p>
<p>Scans like this can provide you with the information you need to fine tune your diet and exercise regimen, also helping you navigate any changes or tweaks you may need to make to ensure your heart is healthy, gets healthy, and/or remains healthy.<sup>13</sup></p>`
  },
  imageSrc: {
    type: String,
    default: '/assets/images/Coronary-mens.jpg'
  },
  imageAlt: {
    type: String,
    default: 'Coronary calcium scan on a medical monitor'
  },
  imageWidth: {
    type: Number,
    default: 320
  },
  imageHeight: {
    type: Number,
    default: 446
  },
  imagePosition: {
    type: String,
    default: 'left',
    validator: (value) => ['left', 'right'].includes(value)
  },
  contentGap: {
    type: String,
    default: 'large',
    validator: (value) => ['large', 'medium'].includes(value)
  },
  showDivider: {
    type: Boolean,
    default: true
  },
  paddingTop: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'reduced'].includes(value)
  },
  paddingBottom: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Badge Image Text Split',
  inputs: [
    {
      name: 'badgeText',
      type: 'text',
      defaultValue: 'Scans and Tests Focus',
      helperText: 'Optional badge label above the title'
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: 'Coronary Calcium Scan'
    },
    {
      name: 'content',
      type: 'richText',
      defaultValue: `<p>You know the saying… "An ounce of prevention is worth a pound of cure."</p>
<p>And, this is essentially what a coronary calcium scan is.</p>
<p>This type of scan is basically a CT scan of your heart aimed at detecting early plaque buildup and producing a score which indicates your personal risk of heart attack.</p>
<p>Scans like this can provide you with the information you need to fine tune your diet and exercise regimen, also helping you navigate any changes or tweaks you may need to make to ensure your heart is healthy, gets healthy, and/or remains healthy.<sup>13</sup></p>`
    },
    {
      name: 'imageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/Coronary-mens.jpg'
    },
    {
      name: 'imageAlt',
      type: 'text',
      defaultValue: 'Coronary calcium scan on a medical monitor'
    },
    {
      name: 'imageWidth',
      type: 'number',
      defaultValue: 320
    },
    {
      name: 'imageHeight',
      type: 'number',
      defaultValue: 446
    },
    {
      name: 'imagePosition',
      type: 'text',
      enum: ['left', 'right'],
      defaultValue: 'left',
      helperText: 'Image position on desktop'
    },
    {
      name: 'contentGap',
      type: 'text',
      enum: ['large', 'medium'],
      defaultValue: 'large'
    },
    {
      name: 'showDivider',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'default',
      helperText: 'Use reduced when stacking below another block with divider'
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'default'
    }
  ]
})
</script>
